
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Pencil, Trash, UserCheck } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Separator } from '@/components/ui/separator';
import { 
  Table, 
  TableHeader, 
  TableBody, 
  TableHead, 
  TableRow, 
  TableCell 
} from '@/components/ui/table';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Avatar } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { 
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';
import { UserProfileWithRoles, UserRole } from '@/types/user';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink } from '@/components/ui/breadcrumb';

const UserManagement = () => {
  const { user: currentUser, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState<UserProfileWithRoles[]>([]);
  const [loading, setLoading] = useState(true);
  const [openUserDialog, setOpenUserDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserProfileWithRoles | null>(null);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserDisplayName, setNewUserDisplayName] = useState('');
  const [newUserBio, setNewUserBio] = useState('');
  const [newUserLinkedIn, setNewUserLinkedIn] = useState('');
  const [newUserRole, setNewUserRole] = useState<UserRole>('author');
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');

  useEffect(() => {
    if (!isAdmin) {
      navigate('/admin/dashboard');
      return;
    }
    
    fetchUsers();
  }, [isAdmin, navigate]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      
      // Fetch user profiles
      const { data: profiles, error: profilesError } = await supabase
        .from('user_profiles')
        .select('*')
        .order('display_name', { ascending: true });
        
      if (profilesError) throw profilesError;
      
      // Fetch roles for all users
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id, role');
        
      if (rolesError) throw rolesError;
      
      // Map roles to user profiles
      const usersWithRoles = profiles.map(profile => {
        const userRoles = roles
          .filter(r => r.user_id === profile.id)
          .map(r => r.role as UserRole);
          
        return {
          ...profile,
          roles: userRoles
        } as UserProfileWithRoles;
      });
      
      setUsers(usersWithRoles);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.display_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.bio?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleCreateUser = async () => {
    if (!newUserEmail || !newUserDisplayName) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    try {
      // Create user with invite only, no password required since we're using Microsoft SSO
      const { data: authData, error: signUpError } = await supabase.auth.admin.createUser({
        email: newUserEmail,
        email_confirm: true,
        user_metadata: {
          display_name: newUserDisplayName
        }
      });
      
      if (signUpError) throw signUpError;
      
      // Update additional profile fields if provided
      if (newUserBio || newUserLinkedIn) {
        const { error: profileError } = await supabase
          .from('user_profiles')
          .update({
            bio: newUserBio || null,
            linkedin_url: newUserLinkedIn || null
          })
          .eq('id', authData.user.id);
          
        if (profileError) throw profileError;
      }
      
      // Update role if not the default author role
      if (newUserRole !== 'author') {
        // Delete existing roles first
        const { error: deleteRoleError } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', authData.user.id);
          
        if (deleteRoleError) throw deleteRoleError;
        
        // Insert new role
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({
            user_id: authData.user.id,
            role: newUserRole
          });
          
        if (roleError) throw roleError;
      }
      
      toast.success('User created successfully. They can now log in with Microsoft 365.');
      setOpenUserDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error('Failed to create user');
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser || !newUserDisplayName) {
      toast.error('Display name is required');
      return;
    }
    
    try {
      // Update profile
      const { error: profileError } = await supabase
        .from('user_profiles')
        .update({
          display_name: newUserDisplayName,
          bio: newUserBio || null,
          linkedin_url: newUserLinkedIn || null
        })
        .eq('id', selectedUser.id);
        
      if (profileError) throw profileError;
      
      // Update role if different from current
      if (!selectedUser.roles.includes(newUserRole)) {
        // Delete existing roles
        const { error: deleteRoleError } = await supabase
          .from('user_roles')
          .delete()
          .eq('user_id', selectedUser.id);
          
        if (deleteRoleError) throw deleteRoleError;
        
        // Add new role
        const { error: roleError } = await supabase
          .from('user_roles')
          .insert({
            user_id: selectedUser.id,
            role: newUserRole
          });
          
        if (roleError) throw roleError;
      }
      
      toast.success('User updated successfully');
      setOpenUserDialog(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    try {
      // Delete the auth user - cascades to profile and roles
      const { error } = await supabase.auth.admin.deleteUser(selectedUser.id);
      
      if (error) throw error;
      
      toast.success('User deleted successfully');
      setOpenDeleteDialog(false);
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    }
  };

  const resetForm = () => {
    setNewUserEmail('');
    setNewUserDisplayName('');
    setNewUserBio('');
    setNewUserLinkedIn('');
    setNewUserRole('author');
    setSelectedUser(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogMode('create');
    setOpenUserDialog(true);
  };

  const openEditDialog = (user: UserProfileWithRoles) => {
    setSelectedUser(user);
    setNewUserDisplayName(user.display_name);
    setNewUserBio(user.bio || '');
    setNewUserLinkedIn(user.linkedin_url || '');
    setNewUserRole(user.roles[0] || 'author');
    setDialogMode('edit');
    setOpenUserDialog(true);
  };

  const confirmDelete = (user: UserProfileWithRoles) => {
    setSelectedUser(user);
    setOpenDeleteDialog(true);
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <Breadcrumb>
        <BreadcrumbItem>
          <BreadcrumbLink href="/admin">Dashboard</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbItem>User Management</BreadcrumbItem>
      </Breadcrumb>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>User Management</CardTitle>
          <Button onClick={openCreateDialog}>
            <Plus className="mr-2 h-4 w-4" /> Add User
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between mb-6">
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
            />
          </div>

          {loading ? (
            <div className="flex justify-center py-8">
              <div className="w-8 h-8 border-4 border-edgefi-purple/30 border-t-edgefi-purple rounded-full animate-spin"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>User</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Bio</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                      No users found
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredUsers.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <Avatar className="h-9 w-9 bg-edgefi-amethyst/20 text-edgefi-purple">
                            {user.avatar_url ? (
                              <img src={user.avatar_url} alt={user.display_name} />
                            ) : (
                              <span>{user.display_name[0]}</span>
                            )}
                          </Avatar>
                          <div>
                            <div className="font-medium">{user.display_name}</div>
                            {user.linkedin_url && (
                              <a 
                                href={user.linkedin_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-xs text-blue-600 hover:underline"
                              >
                                LinkedIn
                              </a>
                            )}
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.roles.map(role => (
                          <Badge key={role} className="mr-1">
                            {role}
                          </Badge>
                        ))}
                      </TableCell>
                      <TableCell className="max-w-xs truncate">
                        {user.bio || "No bio available"}
                      </TableCell>
                      <TableCell>
                        <div className="flex space-x-2">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => openEditDialog(user)}
                            disabled={user.id === currentUser?.id}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => confirmDelete(user)}
                            disabled={user.id === currentUser?.id}
                          >
                            <Trash className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* User Create/Edit Dialog */}
      <Dialog open={openUserDialog} onOpenChange={setOpenUserDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'create' ? 'Create New User' : 'Edit User'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {dialogMode === 'create' && (
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="user@example.com"
                />
                <p className="text-sm text-gray-500">
                  The user will authenticate using Microsoft 365 SSO.
                </p>
              </div>
            )}
            
            <div className="space-y-2">
              <Label htmlFor="displayName">Display Name</Label>
              <Input
                id="displayName"
                value={newUserDisplayName}
                onChange={(e) => setNewUserDisplayName(e.target.value)}
                placeholder="Jane Doe"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="bio">Bio</Label>
              <Textarea
                id="bio"
                value={newUserBio}
                onChange={(e) => setNewUserBio(e.target.value)}
                placeholder="A short description about the user"
                rows={3}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="linkedin">LinkedIn URL</Label>
              <Input
                id="linkedin"
                value={newUserLinkedIn}
                onChange={(e) => setNewUserLinkedIn(e.target.value)}
                placeholder="https://linkedin.com/in/username"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="role">Role</Label>
              <Select
                value={newUserRole}
                onValueChange={(value) => setNewUserRole(value as UserRole)}
              >
                <SelectTrigger id="role">
                  <SelectValue placeholder="Select a role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="author">Author</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenUserDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={dialogMode === 'create' ? handleCreateUser : handleUpdateUser}
              className="flex items-center"
            >
              {dialogMode === 'create' && <UserCheck className="mr-2 h-4 w-4" />}
              {dialogMode === 'create' ? 'Create Microsoft User' : 'Update User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={openDeleteDialog} onOpenChange={setOpenDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the user 
              <span className="font-semibold"> {selectedUser?.display_name}</span> 
              and all their data. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDeleteUser}
              className="bg-red-600 text-white hover:bg-red-700"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default UserManagement;
